import React, { createContext, useContext, useState, useCallback } from "react";
import {
  trackUniqueVisitor,
  updateSelfEnrollmentStat,
} from "_graphql/mutations";
import queryString from "query-string";

const StatsContext = createContext();

// Helper function to detect device type
const getDeviceType = () => {
  const userAgent = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
    return "tablet";
  }
  if (
    /mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(
      userAgent
    )
  ) {
    return "mobile";
  }
  return "desktop";
};

// Helper function to get browser info
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  let browser = "unknown";

  if (userAgent.indexOf("Chrome") > -1) browser = "Chrome";
  else if (userAgent.indexOf("Safari") > -1) browser = "Safari";
  else if (userAgent.indexOf("Firefox") > -1) browser = "Firefox";
  else if (userAgent.indexOf("Edge") > -1) browser = "Edge";
  else if (userAgent.indexOf("Opera") > -1) browser = "Opera";

  return browser;
};

// Helper function to get OS info
const getOSInfo = () => {
  const userAgent = navigator.userAgent;
  let os = "unknown";

  if (userAgent.indexOf("Windows") > -1) os = "Windows";
  else if (userAgent.indexOf("Mac") > -1) os = "macOS";
  else if (userAgent.indexOf("Linux") > -1) os = "Linux";
  else if (userAgent.indexOf("Android") > -1) os = "Android";
  else if (userAgent.indexOf("iOS") > -1) os = "iOS";

  return os;
};

// Helper function to generate device fingerprint
const generateDeviceFingerprint = () => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx.textBaseline = "top";
  ctx.font = "14px Arial";
  ctx.fillText("Device fingerprint", 2, 2);

  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    window.screen.width + "x" + window.screen.height,
    new Date().getTimezoneOffset(),
    canvas.toDataURL(),
  ].join("|");

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash).toString(36);
};

// Helper function to get URL parameters
const getUrlParameters = () => {
  const params = queryString.parse(window.location.search);
  return {
    source: params.utm_source || "direct",
    medium: params.utm_medium || "none",
    campaign: params.utm_campaign || null,
    referrer: document.referrer || null,
  };
};

// Helper function to generate session ID
const generateSessionId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Create a provider component
export const StatsProvider = ({ children, client }) => {
  const [visitorId, setVisitorId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [sessionId] = useState(() => {
    // Check if session ID exists in sessionStorage
    let sessionId = sessionStorage.getItem("paypath_session_id");
    if (!sessionId) {
      sessionId = generateSessionId();
      sessionStorage.setItem("paypath_session_id", sessionId);
    }
    return sessionId;
  });
  const [pageStartTime, setPageStartTime] = useState(Date.now());

  // Initialize visitor tracking - call this manually with selfEnrollmentId
  // useEffect(() => {
  //   initializeVisitorTracking();
  // }, []);

  // Track unique visitor and get visitor ID
  const initializeVisitorTracking = useCallback(
    async (selfEnrollmentId) => {
      // Prevent duplicate calls for the same session
      if (
        isInitialized ||
        visitorId ||
        sessionStorage.getItem("paypath_visitor_id")
      ) {
        return;
      }

      // Don't track in preview mode
      const params = queryString.parse(window.location.search);
      if (params?.preview === "paypath") {
        return;
      }

      try {
        setIsInitialized(true);
        const urlParams = getUrlParameters();
        const deviceFingerprint = generateDeviceFingerprint();

        const visitorData = {
          self_enrollment_id: selfEnrollmentId,
          user_agent: navigator.userAgent,
          device_fingerprint: deviceFingerprint,
          device_type: getDeviceType(),
          browser: getBrowserInfo(),
          os: getOSInfo(),
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
          source: urlParams.source,
          medium: urlParams.medium,
          campaign: urlParams.campaign,
          referrer: urlParams.referrer,
          landing_page: window.location.pathname,
          session_id: sessionId,
        };

        const result = await client.mutate({
          mutation: trackUniqueVisitor,
          variables: visitorData,
        });

        if (result.data?.trackUniqueVisitor) {
          setVisitorId(result.data.trackUniqueVisitor);
          // Store visitor ID in sessionStorage for this session
          sessionStorage.setItem(
            "paypath_visitor_id",
            result.data.trackUniqueVisitor
          );
        }
      } catch (error) {
        console.error("Error tracking unique visitor:", error);
        setIsInitialized(false); // Reset on error to allow retry
      }
    },
    [isInitialized, visitorId, sessionId, client]
  );

  // Function to update self enrollment stats
  const updateStats = async (statsData) => {
    try {
      // Don't update stats in preview mode
      const params = queryString.parse(window.location.search);
      if (params?.preview === "paypath") {
        return false;
      }

      const currentVisitorId =
        visitorId || sessionStorage.getItem("paypath_visitor_id");
      const urlParams = getUrlParameters();

      // Calculate time spent on current page
      const timeSpentOnPage = Math.floor((Date.now() - pageStartTime) / 1000);

      const statData = {
        application_id: statsData.application_id,
        self_enrollment_id: statsData.self_enrollment_id,
        page: statsData.page,
        reached_dead_end: statsData.reached_dead_end || false,
        visitor_id: currentVisitorId,
        device_type: getDeviceType(),
        browser: getBrowserInfo(),
        os: getOSInfo(),
        source: urlParams.source,
        medium: urlParams.medium,
        campaign: urlParams.campaign,
        referrer: urlParams.referrer,
        session_id: sessionId,
        time_spent_on_page: timeSpentOnPage,
        is_completion: statsData.is_completion || false,
        ...statsData, // Allow override of any fields
      };

      const result = await client.mutate({
        mutation: updateSelfEnrollmentStat,
        variables: statData,
      });

      // Reset page start time for next page
      setPageStartTime(Date.now());

      return result.data?.updateSelfEnrollmentStat;
    } catch (error) {
      console.error("Error updating self enrollment stat:", error);
      return false;
    }
  };

  return (
    <StatsContext.Provider
      value={{
        visitorId,
        sessionId,
        updateStats,
        initializeVisitorTracking,
        getDeviceType,
        getBrowserInfo,
        getOSInfo,
      }}
    >
      {children}
    </StatsContext.Provider>
  );
};

// Custom hook to use the stats context
export const useStats = () => {
  const context = useContext(StatsContext);
  if (!context) {
    throw new Error("useStats must be used within a StatsProvider");
  }
  return context;
};
